import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import IdleTimer from 'react-idle-timer';
import { IRootState } from '../../redux';
// Store
import { loginUser } from '../../redux/user/actions';
import { logoutUser } from '../../redux/user/actions';
// Providers
import { getAccessToken, getUserInfo } from '../../services/login.service';
// Helpers
import { loginRedirect, shouldSkipAuth } from '../../helpers/auth';
import { staticConfig } from '../../config/static';
import { FullScreenLoader } from '../../components/Loader/FullScreenLoader';
import { Button, Popup, PopupButtons, PopupContent } from '../../components';

interface IAuthState {
    loaded: boolean;
    timerId?: ReturnType<typeof setTimeout>;
    popupShown: boolean;
}

class _Auth extends React.Component<ReduxType, IAuthState> {
    constructor(props: ReduxType) {
        super(props);
        this.state = {
            loaded: false,
            timerId: null,
            popupShown: false,
        };
    }

    getUser = () => {
        return getUserInfo().then((data) => {
            sessionStorage.setItem('userOrganizationName', data.org_name);
            sessionStorage.setItem('userOrganizationId', '3');
            sessionStorage.setItem('userName', data.name);
            this.props.loginUser(data.sub);
        });
    };

    checkState = (state: string): boolean => {
        const savedState = sessionStorage.getItem('codeState');
        return (savedState === null && state !== null) || savedState === state;
    };

    componentDidMount() {
        const params = new URLSearchParams(window.location.search);

        if (this.props.isAuthorized || shouldSkipAuth()) {
            if (params.get('Login') === 'true') {
                const idptest = params.get('idptest');
                loginRedirect(idptest && idptest === '1');
            }
            this.setState({ loaded: true });
        } else {
            if (params.get('code') && params.get('state') && this.checkState(params.get('state'))) {
                getAccessToken(params.get('code'))
                    .then((res) => {
                        sessionStorage.removeItem('codeVerifier');
                        sessionStorage.removeItem('codeNonce');
                        sessionStorage.removeItem('codeState');

                        localStorage.setItem('isAuthenticated', 'true');

                        this.getUser().then(() => {
                            this.setState({ loaded: true });
                            sessionStorage.removeItem('useTestIdp');
                        });
                    })
                    .catch((error) => {
                        this.props.logoutUser();
                    });
            } else {
                const idptest = params.get('idptest');
                loginRedirect(idptest && idptest === '1');
            }
        }
    }

    componentWillUnmount() {
        this.stopTimer(this.state.timerId);
    }

    startTimer = (): ReturnType<typeof setTimeout> => {
        return setInterval(
            () => this.handleLogOff(),
            this.getTimeFromMinutesInMilliseconds(staticConfig.minutesBeforeAutoLogOffAfterAutoLogOffPopupShown)
        );
    };

    stopTimer = (timerId: ReturnType<typeof setTimeout>) => {
        clearInterval(timerId);
    };

    handleOnPopupOk = () => {
        this.stopTimer(this.state.timerId);
        this.setState({ popupShown: false });
    };

    getTimeFromMinutesInMilliseconds(minutes: number) {
        return minutes * 60 * 1000;
    }

    public render() {
        return this.state.loaded ? (
            <>
                <IdleTimer
                    timeout={this.getTimeFromMinutesInMilliseconds(
                        staticConfig.inactiveMinutesBeforeAutoLogOffPopupShown
                    )}
                    onIdle={this.handleShowPopup}
                >
                    {this.props.children}
                </IdleTimer>

                {this.state.popupShown ? (
                    <>
                        <Popup show headertext="AUTO_LOGOFF" type="alert">
                            <PopupContent className="d-flex flex-column px-3 my-3">
                                <FormattedMessage
                                    id="AUTO_LOGOFF_BODY"
                                    defaultMessage="AUTO_LOGOFF_BODY"
                                    values={{ minutes: staticConfig.minutesBeforeAutoLogOffAfterAutoLogOffPopupShown }}
                                />
                            </PopupContent>
                            <PopupButtons className="pt-2">
                                <Button type="button" onClick={this.handleOnPopupOk} className="mr-2">
                                    <FormattedMessage id="OK" />
                                </Button>
                            </PopupButtons>
                        </Popup>
                    </>
                ) : null}
            </>
        ) : (
            <FullScreenLoader />
        );
    }

    handleShowPopup = () => {
        this.setState({
            popupShown: true,
            timerId: this.startTimer(),
        });
    };

    handleLogOff = () => {
        this.stopTimer(this.state.timerId);
        this.props.logoutUser();
    };
}

const mapStateToProps = ({ user }: IRootState) => {
    return {
        isAuthorized: user.isAuthorized,
    };
};

const mapDispatcherToProps = (dispatch: Dispatch) => {
    return {
        loginUser: (userId: string) => dispatch(loginUser(userId)),
        logoutUser: () => dispatch(logoutUser()),
    };
};

type ReduxType = ReturnType<typeof mapDispatcherToProps> &
    ReturnType<typeof mapStateToProps> & { children?: React.ReactNode };

export const Auth = connect(mapStateToProps, mapDispatcherToProps)(_Auth);
